import React, { useEffect, useState } from "react";

import { Info } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Tooltip
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { marked } from "marked";

import { methods } from "api/methods";
import { ReleaseNote } from "api/models";
import { resources } from "api/resources";
import { useApi } from "api/useApi";
import { Header2 } from "components/common";
import { InputLabel, TextField } from "components/common/";
import { DateInput } from "components/common/DateInput";
import PrimaryButton from "components/common/PrimaryButton";
import { useTranslations } from "components/useTranslations";

import ReleaseNotesDetailsFormMarkdownEditor from "./ReleaseNotesDetailsFormMarkdownEditor";
import useReleaseNotesDetailsFormStyles from "./useReleaseNotesDetailsFormStyles";
import useReleaseNotesDetailsReducer, {
  ReleaseNotesDetailsActionType
} from "./useReleaseNotesDetailsReducer";

export type ReleaseNotesDetailsFormProps = {
  releaseNote?: ReleaseNote;
  getReleaseNotesStateHandler?: () => void;
};

const ReleaseNotesDetailsForm: React.FC<
  ReleaseNotesDetailsFormProps
> = props => {
  const styles = useReleaseNotesDetailsFormStyles();
  const execute = useApi();
  const { releaseNote } = props;
  const t = useTranslations();
  const [state, dispatch] = useReleaseNotesDetailsReducer();
  const [isBusy, setIsBusy] = useState(false);
  // On page load logic
  useEffect(() => {
    if (releaseNote) {
      if (releaseNote?.key) {
        dispatch({
          type: ReleaseNotesDetailsActionType.ON_PAGE_LOAD,
          payload: {
            key: releaseNote?.key,
            formDetails: {
              releaseNotesVersion: releaseNote?.version ?? "",
              releaseNotesDate: releaseNote?.releaseDate ?? "",
              releaseNotesImprovements: releaseNote?.improvements ?? "",
              releaseNotesFixes: releaseNote?.fixes ?? ""
            },
            validationDetails: {
              releaseNotesVersionValidationMessage: "",
              releaseNotesDateValidationMessage: "",
              releaseNotesImprovementsValidationMessage: "",
              releaseNotesFixesValidationMessage: ""
            }
          }
        });
      } else {
        dispatch({
          type: ReleaseNotesDetailsActionType.ON_PAGE_LOAD,
          payload: {
            key: releaseNote?.key,
            formDetails: {
              releaseNotesVersion: releaseNote?.version ?? "",
              releaseNotesDate: `${(
                new Date().getMonth() + 1
              ).toString()}/${new Date().getDate().toString()}/${new Date()
                .getFullYear()
                .toString()}`,
              releaseNotesImprovements: releaseNote?.improvements ?? "",
              releaseNotesFixes: releaseNote?.fixes ?? ""
            }
          }
        });
      }
    }
  }, [releaseNote, dispatch]);

  // Save Button
  const save = () => {
    setIsBusy(true);
    const formValues: ReleaseNote = {
      key: releaseNote?.key ?? "",
      version: state.formDetails?.releaseNotesVersion,
      releaseDate: state.formDetails?.releaseNotesDate,
      improvements: state.formDetails?.releaseNotesImprovements,
      fixes: state.formDetails?.releaseNotesFixes
    };

    dispatch({
      type: ReleaseNotesDetailsActionType.IS_LOADING
    });

    execute(
      releaseNote?.key
        ? `${resources.releaseNote(releaseNote?.key)}`
        : `${resources.releaseNotes}/create`,
      releaseNote?.key ? methods.PUT : methods.POST,
      formValues
    )
      .then((response: ReleaseNote) => {
        dispatch({
          type: ReleaseNotesDetailsActionType.CHANGE_FORM_STATE,
          payload: {
            key: "validationDetails",
            value: {
              ...state.validationDetails,
              releaseNotesVersionValidationMessage:
                response.versionValidationMessage ?? "",
              releaseNotesDateValidationMessage:
                response.dateValidationMessage ?? ""
            }
          }
        });
        if (response?.isInvalid) {
          setSuccess(false);
          return;
        }
        setSuccess(true);
        if (props.getReleaseNotesStateHandler !== undefined) {
          props.getReleaseNotesStateHandler();
        }
        setIsBusy(false);
      })
      .catch(() => {
        setIsBusy(false);
      });
  };

  // State whether form successful
  const [success, setSuccess] = useState(false);

  const handleClose = (
    event: React.SyntheticEvent<any> | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
  };

  const getMarkdownPreview = (markdownText: string) => {
    const parsedMarkdown = marked(markdownText, {
      gfm: true
      // This cast trims the possible response types down to just string, as the
      // result of this call to marked will always be a string and will not be a
      // Promise<string>
    }) as string;

    return (
      <div
        className={styles.previewBox}
        // This is labeled as dangerous because you are potentially allowing
        // unvetted HTML to be directly inserted into the DOM of your page. In
        // our use here, we are not concerned with this possibility because we
        // control the HTML that is being created and inserted into the page.
        dangerouslySetInnerHTML={{
          __html: parsedMarkdown
        }}
      />
    );
  };

  return (
    <div
      className={
        releaseNote?.key
          ? styles.masterContainerEdit
          : styles.masterContainerCreate
      }
    >
      <Paper className={styles.pageContent}>
        <form className={styles.form}>
          <Grid container>
            <Grid size={{ xs: 12 }} className={styles.spaceChildrenApart}>
              <Header2>{t`__RELEASE_NOTES_DETAILS_SUBTITLE`}</Header2>
              <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={success}
                autoHideDuration={6000}
                onClose={handleClose}
              >
                <Alert
                  onClose={handleClose}
                  severity="success"
                  variant="filled"
                >
                  {t`__RELEASE_NOTES_DETAILS_SAVED`}
                </Alert>
              </Snackbar>
              <Grid>
                <Tooltip arrow title={t`__RELEASE_NOTES_DETAILS_MARKDOWN_INFO`}>
                  <IconButton
                    href="https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax"
                    target="_blank"
                  >
                    <Info />
                  </IconButton>
                </Tooltip>
                <PrimaryButton
                  disabled={!state.isFormDirty || isBusy}
                  onClick={save}
                  name="saveReleaseNotes"
                >
                  {t`__RELEASE_NOTES_DETAILS_SAVE`}
                </PrimaryButton>
              </Grid>
            </Grid>
          </Grid>
          <Grid className={styles.rowOfInputs} container>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                className={styles.input}
                size="small"
                variant="outlined"
                label={t`__RELEASE_NOTES_DETAILS_NAME`}
                error={
                  state.validationDetails
                    ?.releaseNotesVersionValidationMessage !== ""
                }
                helperText={
                  state.validationDetails?.releaseNotesVersionValidationMessage
                }
                inputProps={{ maxLength: 50 }}
                onChange={e => {
                  dispatch({
                    type: ReleaseNotesDetailsActionType.CHANGE_FORM_FIELD,
                    payload: {
                      key: "releaseNotesVersion",
                      value: e.target.value
                    }
                  });
                }}
                value={state.formDetails?.releaseNotesVersion}
              />
            </Grid>
          </Grid>
          <Grid className={styles.rowOfInputs} container>
            <Grid size={{ xs: 12, md: 4 }}>
              <InputLabel
                className={styles.label}
                id="release-date-select-label"
              >
                {t`__RELEASE_NOTES_DETAILS_RELEASE_DATE`}
              </InputLabel>
              <DateInput
                format={releaseNote?.dateFormatString}
                readOnlyInput
                onChange={value => {
                  dispatch({
                    type: ReleaseNotesDetailsActionType.CHANGE_FORM_FIELD,
                    payload: {
                      key: "releaseNotesDate",
                      value: value
                    }
                  });
                }}
                value={new Date(state.formDetails?.releaseNotesDate ?? "")}
              />
            </Grid>
          </Grid>
          {state.isFormLoaded && (
            <>
              <Grid className={styles.rowOfInputs} container>
                <Grid container direction="row" className={styles.rowOfInputs}>
                  <Grid className={styles.halfWidth}>
                    <InputLabel id="improvements-select-label">
                      {t`__RELEASE_NOTES_DETAILS_IMPROVEMENTS`}
                    </InputLabel>
                    <Box className={styles.inputBox}>
                      <ReleaseNotesDetailsFormMarkdownEditor
                        value={
                          state.formDetails?.releaseNotesImprovements ?? ""
                        }
                        onChange={(newValue: string) => {
                          dispatch({
                            type: ReleaseNotesDetailsActionType.CHANGE_FORM_FIELD,
                            payload: {
                              key: "releaseNotesImprovements",
                              value: newValue
                            }
                          });
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid className={styles.halfWidth}>
                    <InputLabel
                      id="improvements-preview-label"
                      className={styles.previewLabel}
                    >
                      {t`__RELEASE_NOTES_DETAILS_IMPROVEMENTS_PREVIEW`}
                    </InputLabel>
                    {getMarkdownPreview(
                      state.formDetails?.releaseNotesImprovements ?? ""
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid className={styles.rowOfInputs} container>
                <Grid container direction="row" className={styles.rowOfInputs}>
                  <Grid className={styles.halfWidth}>
                    <InputLabel id="fixes-select-label">
                      {t`__RELEASE_NOTES_DETAILS_FIXES`}
                    </InputLabel>
                    <Box className={styles.inputBox}>
                      <ReleaseNotesDetailsFormMarkdownEditor
                        value={state.formDetails?.releaseNotesFixes ?? ""}
                        onChange={(newValue: string) => {
                          dispatch({
                            type: ReleaseNotesDetailsActionType.CHANGE_FORM_FIELD,
                            payload: {
                              key: "releaseNotesFixes",
                              value: newValue
                            }
                          });
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid className={styles.halfWidth}>
                    <InputLabel
                      id="fixes-preview-label"
                      className={styles.previewLabel}
                    >
                      {t`__RELEASE_NOTES_DETAILS_FIXES_PREVIEW`}
                    </InputLabel>
                    {getMarkdownPreview(
                      state.formDetails?.releaseNotesFixes ?? ""
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </form>
      </Paper>
    </div>
  );
};

export default ReleaseNotesDetailsForm;
